import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import type { Company } from '../types';

interface LicenseInformationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * License Information Modal Component - Premium Full Screen Responsive Design
 * Optimized for Mobile (320px+), HD (1366px), FHD (1920px), and beyond
 */
export const LicenseInformationModal: React.FC<LicenseInformationModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user?.companyId) {
      fetchCompanyInfo();
    }
  }, [isOpen, user?.companyId]);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const companyData = await api.getCompanyById(user?.companyId || '');
      setCompany(companyData);
    } catch (err) {
      console.error('Error fetching company info:', err);
      setError('Gagal mengambil informasi lisensi');
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (!company) return 'Free Trial';
    
    if (!company.subs_type || !company.subs_startdate || !company.subs_enddate) {
      return 'Free Trial';
    }

    const now = new Date();
    const endDate = new Date(company.subs_enddate);
    
    if (now > endDate) {
      return 'Expired';
    }
    
    return 'Active';
  };

  const getSubscriptionTypeLabel = () => {
    if (!company?.subs_type) return 'Free Trial';
    
    const typeMap: Record<string, string> = {
      'free_trial': 'Free Trial',
      'basic': 'Basic Plan',
      'professional': 'Professional Plan',
      'enterprise': 'Enterprise Plan'
    };
    
    return typeMap[company.subs_type] || company.subs_type;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const getDaysRemaining = () => {
    if (!company?.subs_enddate) return null;
    
    const now = new Date();
    const endDate = new Date(company.subs_enddate);
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusConfig = () => {
    const status = getSubscriptionStatus();
    switch (status) {
      case 'Active':
        return {
          color: 'text-emerald-600',
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          gradient: 'from-emerald-500 to-teal-600',
          icon: '✓',
          badge: 'bg-emerald-100 text-emerald-700'
        };
      case 'Expired':
        return {
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          gradient: 'from-red-500 to-pink-600',
          icon: '✕',
          badge: 'bg-red-100 text-red-700'
        };
      default:
        return {
          color: 'text-blue-600',
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          gradient: 'from-blue-500 to-indigo-600',
          icon: '◆',
          badge: 'bg-blue-100 text-blue-700'
        };
    }
  };

  if (!isOpen) return null;

  const statusConfig = getStatusConfig();
  const daysRemaining = getDaysRemaining();

  return (
    <div className="fixed inset-0 z-[9999] animate-fadeIn">
      {/* Backdrop with blur effect */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-slate-900/95 to-gray-900/95 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Main Content Container - Fully Responsive */}
      <div className="relative h-full w-full overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-2 sm:p-4 md:p-6 lg:p-8">
          <div className="relative w-full max-w-[98%] sm:max-w-[95%] md:max-w-3xl lg:max-w-4xl xl:max-w-5xl animate-slideUp">
            
            {/* Close Button - Responsive */}
            <button
              onClick={onClose}
              className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 md:-top-4 md:-right-4 z-10 w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 bg-white rounded-full shadow-2xl flex items-center justify-center hover:bg-gray-100 transition-all duration-300 hover:scale-110 group"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Main Card - Responsive */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
              
              {/* Header Section - Responsive */}
              <div className={`relative bg-gradient-to-r ${statusConfig.gradient} px-4 py-5 sm:px-6 sm:py-6 md:px-7 md:py-7 lg:px-8 lg:py-8 overflow-hidden`}>
                {/* Decorative circles - Responsive */}
                <div className="absolute top-0 right-0 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 bg-white/10 rounded-full -mr-16 sm:-mr-20 md:-mr-24 lg:-mr-28 -mt-16 sm:-mt-20 md:-mt-24 lg:-mt-28" />
                <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 bg-white/10 rounded-full -ml-12 sm:-ml-16 md:-ml-18 lg:-ml-20 -mb-12 sm:-mb-16 md:-mb-18 lg:-mb-20" />
                
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                    <div>
                      <p className="text-white/80 text-xs sm:text-sm font-medium uppercase tracking-wider mb-1">License Information</p>
                      <h1 className="text-white text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold break-words">{company?.nama || 'Loading...'}</h1>
                    </div>
                    
                    {/* Status Badge - Responsive */}
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full">
                      <span className="text-white font-semibold text-sm sm:text-base md:text-lg">{getSubscriptionStatus()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Section - Responsive */}
              <div className="p-4 sm:p-5 md:p-6 lg:p-7">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
                    <div className="relative">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full"></div>
                      </div>
                    </div>
                    <p className="mt-4 sm:mt-6 text-gray-600 font-medium text-sm sm:text-base">Loading license information...</p>
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                      <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-red-600 font-semibold text-base sm:text-lg text-center px-4">{error}</p>
                    <button
                      onClick={fetchCompanyInfo}
                      className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
                    >
                      Try Again
                    </button>
                  </div>
                ) : company ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                    
                    {/* Left Column - Subscription Plan - Responsive */}
                    <div className="lg:col-span-2 space-y-4 sm:space-y-5">
                      
                      {/* Subscription Plan Card - Responsive */}
                      <div className={`relative bg-gradient-to-br ${statusConfig.gradient} rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 text-white overflow-hidden shadow-xl`}>
                        <div className="absolute top-0 right-0 w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-white/10 rounded-full -mr-12 sm:-mr-14 md:-mr-16 -mt-12 sm:-mt-14 md:-mt-16" />
                        <div className="relative z-10">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                            <div>
                              <p className="text-white/80 text-xs sm:text-sm font-medium uppercase tracking-wider mb-1 sm:mb-2">Current Plan</p>
                              <h2 className="text-lg sm:text-xl md:text-2xl font-bold">
                                {getSubscriptionTypeLabel()}
                              </h2>
                            </div>
                            <div className={`px-3 py-1.5 sm:px-4 sm:py-2 ${statusConfig.badge} rounded-full font-semibold text-sm sm:text-base`}>
                              {getSubscriptionStatus()}
                            </div>
                          </div>
                          
                          {/* Plan Features - Responsive Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
                              <p className="text-white/70 text-xs sm:text-sm mb-1">Start Date</p>
                              <p className="text-white font-semibold text-sm sm:text-base md:text-lg">{formatDate(company.subs_startdate)}</p>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4">
                              <p className="text-white/70 text-xs sm:text-sm mb-1">End Date</p>
                              <p className="text-white font-semibold text-sm sm:text-base md:text-lg">{formatDate(company.subs_enddate)}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Days Remaining Card - Responsive */}
                      {company.subs_enddate && daysRemaining !== null && (
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-gray-200">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                              <p className="text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wider mb-1 sm:mb-2">Time Remaining</p>
                              <div className="flex items-baseline gap-2">
                                <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">{daysRemaining}</span>
                                <span className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-600">days</span>
                              </div>
                            </div>
                            <div className="text-left sm:text-right w-full sm:w-auto">
                              {daysRemaining <= 30 && daysRemaining > 0 && (
                                <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-semibold text-xs sm:text-sm">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  Expiring Soon
                                </div>
                              )}
                              {daysRemaining === 0 && (
                                <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-semibold text-xs sm:text-sm">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  Expired
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Progress Bar - Responsive */}
                          {company.subs_startdate && company.subs_enddate && (() => {
                            const start = new Date(company.subs_startdate).getTime();
                            const end = new Date(company.subs_enddate).getTime();
                            const now = Date.now();
                            const total = end - start;
                            const elapsed = now - start;
                            const progress = Math.min(Math.max((elapsed / total) * 100, 0), 100);
                            
                            return (
                              <div className="mt-4 sm:mt-6">
                                <div className="h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full bg-gradient-to-r ${statusConfig.gradient} transition-all duration-500`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <div className="flex justify-between mt-2 text-xs text-gray-500">
                                  <span>Started</span>
                                  <span>{progress.toFixed(0)}% elapsed</span>
                                  <span>Ends</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Company Details - Responsive */}
                      {company.alamat && (
                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200">
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-600 text-xs sm:text-sm font-medium mb-1">Company Address</p>
                              <p className="text-gray-900 font-medium text-sm sm:text-base break-words">{company.alamat}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Quick Info & Actions - Responsive */}
                    <div className="space-y-4 sm:space-y-5">
                      
                      {/* Quick Stats - Responsive */}
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Quick Stats</h3>
                        <div className="space-y-3 sm:space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-xs sm:text-sm">License Type</span>
                            <span className="font-semibold text-gray-900 text-xs sm:text-sm text-right">{getSubscriptionTypeLabel()}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-xs sm:text-sm">Status</span>
                            <span className={`font-semibold ${statusConfig.color} text-xs sm:text-sm`}>{getSubscriptionStatus()}</span>
                          </div>
                          {daysRemaining !== null && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600 text-xs sm:text-sm">Days Left</span>
                              <span className="font-semibold text-gray-900 text-xs sm:text-sm">{daysRemaining}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Support Card - Responsive */}
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Need Help?</h3>
                        <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">
                          For subscription inquiries, upgrades, or support, contact our sales team.
                        </p>
                        <a href='mailto:info@terretech.id' className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 sm:py-3 px-4 rounded-lg sm:rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Contact Sales
                        </a>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Footer - Responsive */}
              <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                  <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                    © 2026 <a href="https://terretech.id" target='_blank'>Smartwatch Monitoring Powered by Terretech Nusantara</a>. All rights reserved.
                  </p>
                  <button
                    onClick={onClose}
                    className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg sm:rounded-xl transition-colors duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }

        /* Responsive breakpoints optimization */
        @media (max-width: 640px) {
          /* Mobile optimizations */
          .animate-slideUp {
            animation: slideUp 0.3s ease-out;
          }
        }

        @media (min-width: 1366px) and (max-width: 1920px) {
          /* HD (1366px) optimizations */
          /* Content is already optimized with Tailwind classes */
        }

        @media (min-width: 1920px) {
          /* FHD (1920px) and above optimizations */
          /* Content scales beautifully with max-width constraints */
        }
      `}</style>
    </div>
  );
};
